import useSearchParam from "hooks/useSearchParam";
import useGoogleInviteSignUp from "hooks/useGoogleInviteSignUp";
import {useLangParamPreselect} from "hooks/useLangParamPreselect";
import React, {useEffect, useMemo, useState} from "react";
import {T} from "@tolgee/react";
import {IGetInfoAboutInviteTokenRes} from "@evennt-tech/api-v2-external";
import {apiV2} from "api/firebase";
import SignUpPresentation from "view/SignUp/SignUpEmail/presentation";
import {generatePath, useNavigate} from "react-router-dom";
import {
  APP_PATH,
  LOGIN_PATH,
  SHOW_EXHIBITOR_QUICK_CART_PATH,
} from "view/routes";
import {Box, CircularProgress} from "@material-ui/core";
import useStyles from "view/SignUp/SignUpExhibitor/style";
import {jwtDecode} from "jwt-decode";
import TwoFactorChallenge from "view/components/TwoFactorChallenge";
import {loginUser} from "tools/auth/loginUser";
import {quickOrderIdParamName} from "tools/routing";

export const SignUpExhibitor = () => {
  const classes = useStyles();

  useLangParamPreselect();

  const token = useSearchParam("token");
  const history = useNavigate();

  const quickOrderPath = useMemo(() => {
    if (!token) return undefined;
    try {
      const decoded = jwtDecode<{
        showId?: string;
        exhibitorPairId?: string;
        quickOrderId?: string;
      }>(token);
      if (decoded.showId && decoded.exhibitorPairId && decoded.quickOrderId) {
        return `${generatePath(SHOW_EXHIBITOR_QUICK_CART_PATH, {
          showId: decoded.showId,
          exhibitorPairId: decoded.exhibitorPairId,
        })}?${quickOrderIdParamName}=${decoded.quickOrderId}`;
      }
    } catch {
      console.error("Invalid token: ", token);
    }
    return undefined;
  }, [token]);
  const [
    infoAboutToken,
    setInfoAboutToken,
  ] = useState<IGetInfoAboutInviteTokenRes | null>(null);
  const [error, setError] = useState(false);
  // Captured during render, before the Google hook's effect strips the
  // fragment — an already-accepted invite must not bounce the person to /login
  // while the callback is still being handled.
  const [isGoogleCallback] = useState(() =>
    window.location.hash.includes("provider=google")
  );

  const {
    isBusy,
    googleError,
    isInviteInvalid,
    twoFactor,
    startGoogleSignUp,
    completeSignIn,
    resetTwoFactor,
  } = useGoogleInviteSignUp({
    returnPath: "/sign-up/exhibitor",
    redirectTo: quickOrderPath,
  });

  useEffect(() => {
    const getInfo = async () => {
      if (!token) return;

      try {
        const me = await apiV2.user.getMeSafe().catch(() => null);

        const info = await apiV2.exhibitor
          .signUp({
            token,
            baseURL: import.meta.env.VITE_API_V2_URL as string,
          })
          .getInfoAboutInvite();

        if (me) {
          if (info.alreadyInvited && me.id === info.exhibitorUser.userId) {
            history(`${APP_PATH}?view=invited`, {replace: true});
            return;
          }
          return;
        }

        if (info.alreadyInvited && !isGoogleCallback) {
          const loginUrl = quickOrderPath
            ? `${LOGIN_PATH}?redirectPathAfterLogin=${encodeURIComponent(
                quickOrderPath
              )}`
            : LOGIN_PATH;
          window.location.replace(loginUrl);
          return;
        }

        setInfoAboutToken(info);
      } catch (err) {
        console.error("Failed to get invite info:", err);
        setError(true);
      }
    };

    getInfo();
  }, [history, isGoogleCallback, quickOrderPath, token]);

  const onSubmit = async (password: string): Promise<void> => {
    if (!token || !infoAboutToken) return;

    await apiV2.exhibitor
      .signUp({
        token,
        baseURL: import.meta.env.VITE_API_V2_URL as string,
      })
      .setPasswordAndAcceptInvite({password});

    const email = infoAboutToken.exhibitorUser.user?.email as string;

    await loginUser({
      email,
      password,

      ...(quickOrderPath && {
        redirectTo: quickOrderPath,
      }),
    });
  };

  if (!token) {
    return (
      <div>
        <T keyName="global.sign_up.something_went_wrong" />
      </div>
    );
  }
  if (error || isInviteInvalid) {
    return (
      <SignUpPresentation
        onSubmit={onSubmit}
        showLanguagePicker
        isInviteInvalid
      />
    );
  }
  if (twoFactor) {
    return (
      <TwoFactorChallenge
        pendingToken={twoFactor.pendingToken}
        method={twoFactor.method}
        methods={twoFactor.methods}
        onComplete={completeSignIn}
        onCancel={resetTwoFactor}
      />
    );
  }
  if (!infoAboutToken) {
    return (
      <Box className={classes.loaderBlock}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <SignUpPresentation
      showName={infoAboutToken.showName}
      email={infoAboutToken.exhibitorUser.user?.email}
      contactEmail={infoAboutToken.contactEmail}
      onSubmit={onSubmit}
      showLanguagePicker
      onGoogleSignUp={startGoogleSignUp}
      isGoogleBusy={isBusy}
      googleError={googleError}
    />
  );
};

export default SignUpExhibitor;
